'use client';

/**
 * TestimonialCard — single testimonial card for the landing grid (SPEC-136 §5).
 *
 * Card structure (VERBATIM from §5 ASCII mockup):
 *   - Author photo (64x64 rounded) + name + tier badge + country flag.
 *   - Testimonial text (line-clamp-4, "Ver más" button if longer).
 *   - Media (photo/video thumbnail with play button overlay).
 *   - Metrics row: monthsActive + directReferrals + totalEarnings + networkSize.
 *   - CTA: "Ver perfil de {autor}" → /{username}.
 *
 * Schema.org Review markup (§5):
 *   Each card uses microdata `itemprop="review"` + `itemtype="https://
 *   schema.org/Review"`. The aggregateRating lives at the section level
 *   (handled by TestimonialsSection via JSON-LD script tag).
 *
 * §6 Anti-Invención: ratingValue 5.0 FIXED (not a real rating system —
 * MLM testimonios are positive or absent, per §13).
 *
 * Spec ref: docs/SPEC-136-TESTIMONIOS.md §5 (UI/UX), §13 (§6 Anti-Invención).
 *
 * Anti-P-023: renders real data (no claim PASS).
 * Anti-P-041: 0 `as any` casts.
 * Anti-P-042: 0 `console.*` statements.
 */
import { useState, type ReactElement } from 'react';
import Link from 'next/link';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Play, ExternalLink } from 'lucide-react';
import type { PublicTestimonial, TestimonialTier } from '@profileia/shared';

// ─── Tier metadata (icon + color) ───────────────────────────────────────────

interface TierMeta {
  icon: string;
  label: string;
  color: string;
}

const TIER_META: Record<TestimonialTier, TierMeta> = {
  diamante: { icon: '💎', label: 'Diamante', color: 'text-cyan-600' },
  platino: { icon: '💜', label: 'Platino', color: 'text-purple-600' },
  oro: { icon: '🥇', label: 'Oro', color: 'text-amber-600' },
  plata: { icon: '🥈', label: 'Plata', color: 'text-slate-500 dark:text-slate-400' },
  bronce: { icon: '🥉', label: 'Bronce', color: 'text-orange-700' },
};

// ─── Country flag emoji (ISO 3166-1 alpha-2 → regional indicator symbols) ──

function countryFlagEmoji(isoCode: string): string {
  if (!isoCode || isoCode.length !== 2) return '🏳️';
  const A = 0x1f1e6;
  const base = 'A'.charCodeAt(0);
  const chars = isoCode.toUpperCase().split('').map((c) => {
    return String.fromCodePoint(A + (c.charCodeAt(0) - base));
  });
  return chars.join('');
}

// ─── Avatar with initials fallback ──────────────────────────────────────────

function Avatar({
  photoUrl,
  authorName,
}: {
  photoUrl: string;
  authorName: string;
}): ReactElement {
  const initials = authorName
    .split(' ')
    .map((w) => w.charAt(0).toUpperCase())
    .slice(0, 2)
    .join('');
  if (photoUrl) {
    // eslint-disable-next-line @next/next/no-img-element -- Testimonial photos
    // are user-uploaded (not Next.js Image-optimized assets). Using <img>
    // avoids the next.config.js remotePatterns dance for arbitrary S3 URLs.
    return (
      <img
        src={photoUrl}
        alt={`Foto de ${authorName}`}
        width={64}
        height={64}
        className="rounded-full object-cover w-16 h-16"
        loading="lazy"
      />
    );
  }
  return (
    <div className="rounded-full w-16 h-16 flex items-center justify-center bg-emerald-500 text-white text-xl font-bold">
      {initials || '?'}
    </div>
  );
}

// ─── Main TestimonialCard component ─────────────────────────────────────────

export interface TestimonialCardProps {
  testimonial: PublicTestimonial;
}

export function TestimonialCard({ testimonial }: TestimonialCardProps): ReactElement {
  const [showFullText, setShowFullText] = useState(false);
  const [showMedia, setShowMedia] = useState(false);

  const tierMeta = TIER_META[testimonial.authorTier] ?? TIER_META.bronce;
  const flag = countryFlagEmoji(testimonial.authorCountry);
  const t = testimonial;

  // Build metrics row — only show fields that are present.
  const metrics: string[] = [];
  metrics.push(`${t.results.monthsActive} ${t.results.monthsActive === 1 ? 'mes activo' : 'meses activo'}`);
  if (t.results.directReferrals !== null && t.results.directReferrals !== undefined) {
    metrics.push(`${t.results.directReferrals} referidos`);
  }
  if (t.results.totalEarnings) {
    metrics.push(`${t.results.totalEarnings} ganados`);
  }
  if (t.results.networkSize !== null && t.results.networkSize !== undefined) {
    metrics.push(`red ${t.results.networkSize}`);
  }
  const metricsLine = metrics.join(' · ');

  // Text truncation: 280 chars in card, full text in modal.
  const CARD_TEXT_LIMIT = 280;
  const isTextLong = t.testimonialText.length > CARD_TEXT_LIMIT;
  const cardText = isTextLong
    ? `${t.testimonialText.slice(0, CARD_TEXT_LIMIT)}…`
    : t.testimonialText;

  // Video embed (lazy-loaded on click — no iframe until user clicks play).
  const videoEmbedUrl = t.videoUrl
    ? (() => {
        // Convert YouTube watch?v=ID to /embed/ID; youtu.be/ID to /embed/ID.
        const ytMatch = t.videoUrl.match(
          /(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
        );
        if (ytMatch && ytMatch[1]) return `https://www.youtube.com/embed/${ytMatch[1]}`;
        // Vimeo: convert vimeo.com/ID to player.vimeo.com/video/ID.
        const vimeoMatch = t.videoUrl.match(/vimeo\.com\/(\d+)/);
        if (vimeoMatch && vimeoMatch[1]) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
        return null;
      })()
    : null;

  return (
    <div
      itemScope
      itemType="https://schema.org/Review"
      className="bg-card border border-border p-6 flex flex-col gap-4 hover:shadow-lg transition-shadow rounded-2xl"
    >
      {/* Schema.org hidden meta tags */}
      <meta itemProp="datePublished" content={t.publishedAt ?? new Date().toISOString()} />
      <span itemProp="author" itemScope itemType="https://schema.org/Person" className="hidden">
        <meta itemProp="name" content={t.authorName} />
      </span>
      <div itemProp="reviewRating" itemScope itemType="https://schema.org/Rating" className="hidden">
 {/* §6 Anti-Invención: ratingValue 5.0 FIXED (not a real rating system). */}
        <meta itemProp="ratingValue" content="5" />
        <meta itemProp="bestRating" content="5" />
      </div>

      {/* Author row */}
      <div className="flex items-center gap-4">
        <Avatar photoUrl={t.authorPhoto} authorName={t.authorName} />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-foreground truncate">{t.authorName}</p>
          <p className={`text-sm ${tierMeta.color}`}>
            {tierMeta.icon} {tierMeta.label} · {flag} {t.authorCountry}
          </p>
        </div>
      </div>

      {/* Testimonial text */}
      <div className="text-foreground text-sm leading-relaxed">
        <p itemProp="reviewBody" className={isTextLong ? 'line-clamp-4' : ''}>
          &ldquo;{cardText}&rdquo;
        </p>
        {isTextLong && (
          <button
            type="button"
            onClick={() => setShowFullText(true)}
            className="mt-2 text-emerald-600 hover:text-emerald-700 text-sm font-medium"
          >
            Ver más →
          </button>
        )}
      </div>

      {/* Media (photo/video thumbnail) */}
      {(t.mediaType === 'photo' || t.mediaType === 'video') && (
        <button
          type="button"
          onClick={() => setShowMedia(true)}
          className="relative w-full aspect-video rounded-lg overflow-hidden bg-muted group"
          aria-label={t.mediaType === 'video' ? 'Reproducir video' : 'Ver foto'}
        >
          {/* eslint-disable-next-line @next/next/no-img-element -- testimonial
              thumbnails are user-uploaded S3 URLs (not Next.js assets). */}
          <img
            src={t.videoThumbnail || t.photoUrl || t.authorPhoto || '/api/placeholder/testimonial'}
            alt={`Media de ${t.authorName}`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
            loading="lazy"
          />
          {t.mediaType === 'video' && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/30">
              <div className="bg-black/50 rounded-full p-3">
                <Play className="w-6 h-6 text-white" fill="white" />
              </div>
            </div>
          )}
        </button>
      )}

      {/* Metrics row */}
      <div className="border-t border-border pt-3 text-xs text-muted-foreground">
        {metricsLine}
      </div>

      {/* CTA row */}
      <div className="flex items-center justify-between gap-2">
        {t.videoUrl && t.mediaType === 'video' && (
          <button
            type="button"
            onClick={() => setShowMedia(true)}
            className="text-xs text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1"
          >
            <Play className="w-3 h-3" /> Ver video
          </button>
        )}
        {t.username && (
          <Link
            href={`/${t.username}`}
            className="text-xs text-muted-foreground hover:text-foreground font-medium flex items-center gap-1 ml-auto"
          >
            Ver perfil de {t.authorName.split(' ')[0]} <ExternalLink className="w-3 h-3" />
          </Link>
        )}
      </div>

      {/* Full-text modal */}
      {showFullText && (
        <Dialog open={showFullText} onOpenChange={setShowFullText}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Testimonio completo de {t.authorName}</DialogTitle>
            </DialogHeader>
            <p className="text-foreground leading-relaxed whitespace-pre-wrap">
              &ldquo;{t.testimonialText}&rdquo;
            </p>
            <div className="mt-4 pt-4 border-t border-border text-sm text-muted-foreground">
              {metricsLine}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Media modal (video iframe or photo lightbox) */}
      {showMedia && (
        <Dialog open={showMedia} onOpenChange={setShowMedia}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>{t.authorName} — {t.mediaType === 'video' ? 'Video' : 'Foto'}</DialogTitle>
            </DialogHeader>
            {t.mediaType === 'video' && videoEmbedUrl ? (
              <div className="aspect-video">
                <iframe
                  src={videoEmbedUrl}
                  title={`Video de ${t.authorName}`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full rounded-lg"
                />
              </div>
            ) : t.photoUrl ? (
              // eslint-disable-next-line @next/next/no-img-element -- photo lightbox
              <img
                src={t.photoUrl}
                alt={`Foto de ${t.authorName}`}
                className="w-full rounded-lg"
              />
            ) : (
              <p className="text-muted-foreground">Media no disponible.</p>
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

export default TestimonialCard;
